import logoPecaAi from '@/assets/images/logo-peca-ai.svg';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useDisclosure } from '@/hooks';
import PecaAiBasketModal from '../PecaAiBasketModal';
import PecaAiOrderModal from '../PecaAiOrderModal';
import { Button, Image, Modal, Tooltip } from 'antd';
import { getUserFromLocalStorage } from '@/utils/user';
import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { Group } from '@/components/_common';
import { ExternalLink, PhoneCall } from 'react-feather';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import {
  useWorkOrderPecaAiBasketDetail,
  useWorkOrderPecaAiHasBuyer,
  useWorkOrderPecaAiVerifyToken,
} from '@/core/domain/workorder/pecaAi/workorder-pecaAi.hooks';

export interface PecaAiProduct {
  basketItemId?: string;
  productId: string;
  amount: number;
  description: string;
  brand?: string;
  image?: string;
  shippingPrice?: number;
  partNumber: string;
  unitPrice: number;
  totalPrice: number;
  deadlineHours?: number | null;
  observation?: string;
  inBasket: boolean;
}

interface PecaAiContextParams {
  orderId?: string;
  basketId?: string;
  products: PecaAiProduct[];
  isLoading: boolean;
  addProduct(newProduct: PecaAiProduct): void;
  removeProduct(basketItemId: string): void;
  postalCode?: string;
  licensePlate?: string;
  cnpj?: string;
}

const PecaAiContext = createContext<PecaAiContextParams>({
  products: [],
  isLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addProduct: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeProduct: () => {},
});

export const usePecaAiContext = () => useContext(PecaAiContext);

let pecaAiBasketItemSequence = 0;

function createPecaAiBasketItemId(productId: string) {
  pecaAiBasketItemSequence += 1;

  return `${productId}-${Date.now()}-${pecaAiBasketItemSequence}`;
}

function getPecaAiProductAmount(product: PecaAiProduct) {
  return Number(product.amount || 0);
}

function getPecaAiProductTotal(product: PecaAiProduct) {
  const totalPrice = Number(product.totalPrice || 0);

  if (totalPrice > 0) {
    return totalPrice;
  }

  return Number(product.unitPrice || 0) * getPecaAiProductAmount(product);
}

function isSamePecaAiProduct(
  product: PecaAiProduct,
  newProduct: PecaAiProduct,
) {
  return (
    product.productId === newProduct.productId &&
    product.inBasket === newProduct.inBasket
  );
}

function mergePecaAiProducts(
  product: PecaAiProduct,
  newProduct: PecaAiProduct,
) {
  return {
    ...product,
    amount:
      getPecaAiProductAmount(product) + getPecaAiProductAmount(newProduct),
    totalPrice:
      getPecaAiProductTotal(product) + getPecaAiProductTotal(newProduct),
  };
}

function groupPecaAiProducts(products: PecaAiProduct[]) {
  return products.reduce<PecaAiProduct[]>((acc, product) => {
    const index = acc.findIndex((item) => isSamePecaAiProduct(item, product));

    if (index === -1) {
      return [...acc, product];
    }

    return acc.map((item, itemIndex) =>
      itemIndex === index ? mergePecaAiProducts(item, product) : item,
    );
  }, []);
}

export default function PecaAiProvider() {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { data: company, companySettings },
  } = useWorkorderDetailContext();

  const isAuthorizedToAccessPecaAi =
    !!companySettings?.isAuthorizedToAccessPecaAi;

  const { employeeId } = getUserFromLocalStorage();
  const { data: employee } = useEmployee(Number(employeeId));

  const [products, setProducts] = useState<PecaAiProduct[]>([]);
  const [basketModalOpened, basketModalAction] = useDisclosure(false, {
    onClose: () => {
      setProducts([]);
      basketDetail.remove();
    },
  });
  const [orderModalOpened, orderModalAction] = useDisclosure(false);

  const disablePecaAi =
    complement?.status !== WorkOrderComplementStatus.Open &&
    !complement?.pecaAiBasketId;

  const { isSuccess } = useWorkOrderPecaAiVerifyToken(
    isAuthorizedToAccessPecaAi,
  );

  const { isSuccess: hasBuyer, ...pecaAiHasBuyer } = useWorkOrderPecaAiHasBuyer(
    company?.cnpj,
    isSuccess && !disablePecaAi,
  );

  const basketDetail = useWorkOrderPecaAiBasketDetail(
    {
      basketId: complement?.pecaAiBasketId,
      document: company?.cnpj,
    },
    basketModalOpened && hasBuyer,
  );

  const postalCode = useMemo(() => {
    return company?.companyAddress.zip;
  }, [company?.companyAddress]);

  const isLoading = basketDetail.isLoading;

  useEffect(() => {
    if (basketModalOpened) {
      const detail = basketDetail.data?.detail;
      const summary = basketDetail.data?.summary;

      const mapped = detail?.products.map((item, index) => {
        const match = summary?.products?.find((p) => p.id === item.productId);

        return {
          basketItemId: `${item.productId}-${index}`,
          description: item.description,
          amount: item.amount,
          productId: item.productId,
          totalPrice: item.finalPrice,
          unitPrice: item.unitPrice,
          partNumber: item.partNumber,
          inBasket: true,
          deadlineHours: match?.deadlineHours ?? null,
          brand: match?.brand,
        };
      });

      setProducts(groupPecaAiProducts(mapped || []));
    }
  }, [basketDetail.dataUpdatedAt, basketModalOpened]);

  const addProduct = useCallback(
    (newProduct: PecaAiProduct) =>
      setProducts((prev) => {
        const index = prev.findIndex((product) =>
          isSamePecaAiProduct(product, newProduct),
        );

        if (index >= 0) {
          return prev.map((product, productIndex) =>
            productIndex === index
              ? mergePecaAiProducts(product, newProduct)
              : product,
          );
        }

        return [
          ...prev,
          {
            ...newProduct,
            basketItemId:
              newProduct.basketItemId ||
              createPecaAiBasketItemId(newProduct.productId),
          },
        ];
      }),
    [setProducts],
  );

  const removeProduct = useCallback(
    (basketItemId: string) =>
      setProducts((prev) =>
        prev.filter((product) => product.basketItemId !== basketItemId),
      ),
    [setProducts],
  );

  const modalSolicitation = () => {
    Modal.confirm({
      icon: null,

      title: (
        <>
          <Image src={logoPecaAi} preview={false} width={60} />
          <p>Acesso à Integração do Peça Aí</p>
        </>
      ),
      okButtonProps: { style: { display: 'none' } },
      content: (
        <>
          <p>
            <strong>{employee?.name}</strong>, sua empresa ainda não tem
            cadastro no Peça.aí.
          </p>

          <p>
            Cadastre-se agora no link abaixo para iniciar suas compras e ganhe
            descontos exclusivos.
          </p>

          <Group gap={10}>
            <ExternalLink size={16} />
            <Group gap={4}>
              <span>Link:</span>
              <strong>
                <a href="https://peca.ai/" target="_blank">
                  https://peca.ai/
                </a>
              </strong>
            </Group>
          </Group>
          <Group gap={10} style={{ marginBottom: 10 }}>
            <PhoneCall size={16} />
            <Group gap={4}>
              <span>Contato:</span>
              <strong>
                <a href="tel:+5511988135570">11 98813-5570</a>
              </strong>
            </Group>
          </Group>

          <p>Liberação em até 48h úteis após solicitação.</p>
        </>
      ),
    });
  };

  const openBasket = () => {
    if (complement?.pecaAiOrderId) {
      orderModalAction.open();
    } else {
      basketModalAction.open();
    }
  };

  if (!isAuthorizedToAccessPecaAi) return null;

  return (
    <PecaAiContext.Provider
      value={{
        basketId: complement?.pecaAiBasketId,
        orderId: complement?.pecaAiOrderId,
        products,
        isLoading,
        addProduct,
        removeProduct,
        postalCode,
        licensePlate: workorder?.vehicle?.licensePlate,
        cnpj: company?.cnpj,
      }}
    >
      <Tooltip title={disablePecaAi && 'Apenas orçamento Aberto'}>
        <Button
          onClick={hasBuyer ? openBasket : modalSolicitation}
          disabled={disablePecaAi}
          loading={pecaAiHasBuyer.isLoading}
        >
          <Image src={logoPecaAi} preview={false} width={60} />
        </Button>
      </Tooltip>

      <PecaAiBasketModal
        opened={basketModalOpened}
        onClose={basketModalAction.close}
        hasBasket={!!complement?.pecaAiBasketId}
        isLoading={isLoading}
      />

      <PecaAiOrderModal
        opened={orderModalOpened}
        onClose={orderModalAction.close}
      />
    </PecaAiContext.Provider>
  );
}
